import React from 'react';
import {
  Typography,
  Box,
  Grid,
  TextField,
  Alert,
  Button,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Tag,
  Layers,
  Inventory,
  Money,
  LocationOn,
  Business,
  Circle,
  Edit as EditIcon,
  ZoomIn as ZoomInIcon,
} from '@mui/icons-material';
import { formatDate } from '../../../utils/formatDate';
import { formatCommercialSaleDate } from '../../../utils/sales/commercial-sale-date';
import { formatSalePricePerGram } from '../../../utils/inventory/salePricePerGram';
import { InventoryItem } from '../../../types/inventory';
import { spacing } from '../../../styles/theme';
import { DetailModal, ConfirmationModal } from '../../ui/Modal';
import { DecimalNumberInput } from '../../ui';
import ImageModal from '../../ui/ImageModal';
import ProductImagePlaceholder from '../../ui/ProductImagePlaceholder';
import { useInventoryDetailModal } from './InventoryDetailModal.hooks';
import { useInventoryBlockHistory } from './useInventoryBlockHistory';
import InventoryBlockHistorySection from './InventoryBlockHistorySection';
import { mapInventoryAvailability } from '../../common/status';
import {
  HeaderSection,
  ProductInfoContainer,
  ImageContainer,
  ProductImage,
  ProductDetails,
  ProductName,
  SkuContainer,
  SkuLabel,
  SkuValue,
  StatusChip,
  InfoCard,
  CardHeader,
  CardTitle,
  CardContent,
  InfoGrid,
  InfoLabel,
  InfoValue,
  TagsContainer,
  TagChip,
  NoTagsText,
  PriceValue,
  EditButton,
  infoCardHeaderSx,
  infoCardTitleSx,
} from './InventoryDetailModal.styles';

/** Sale data shown inside Precio / Estado y Fechas when viewing a sold piece (#110, #112). */
export interface InventoryDetailSaleContext {
  finalPrice: number;
  saleDate: string;
}

export interface InventoryDetailModalProps {
  open: boolean;
  onClose: () => void;
  item: InventoryItem | null;
  onEditItem?: (id: string) => void;
  onSave?: () => void; // Callback after successful save
  onItemUpdate?: (updatedItem: InventoryItem) => void; // Callback to update the item in parent
  /** When true, allows editing warehouse pieces (e.g. Gestión en Almacén with warehouse edit permission) */
  allowEditInWarehouse?: boolean;
  /** When true, allows editing even when item is ASSIGNED (e.g. from Gestión en Sucursal) */
  allowEditInBranch?: boolean;
  /** Bump to refetch block history (e.g. parent searchTrigger after block/unblock) */
  blockHistoryRefreshKey?: number;
  /** When true, renders content only (no modal shell) for embedding in parent layouts */
  embedded?: boolean;
  /** When true, hides edit actions */
  readOnly?: boolean;
  /** When true and editable, open already in edit mode (row pencil). */
  initialEditMode?: boolean;
  /** When set, shows Precio Final and Fecha de venta in the detail cards */
  saleContext?: InventoryDetailSaleContext;
}

const InventoryDetailModal: React.FC<InventoryDetailModalProps> = ({
  open,
  onClose,
  item,
  onEditItem,
  onSave,
  onItemUpdate,
  allowEditInWarehouse = false,
  allowEditInBranch = false,
  blockHistoryRefreshKey,
  embedded = false,
  readOnly = false,
  initialEditMode = false,
  saleContext,
}) => {
  const {
    getStatusColor,
    formatPrice,
    isEditMode,
    canEdit,
    isSaving,
    error,
    formData,
    handleEditClick,
    handleCancelEdit,
    handleSaveClick,
    handleConfirmSave,
    handleFieldChange,
    showConfirmDialog,
    setShowConfirmDialog,
    materials,
    isLoadingMaterials,
    colors,
    isLoadingColors,
    marginInfo,
    tagsInfo,
    propertiesInfo,
    locationInfo,
    datesInfo,
    productImageSrc,
    productImageExpandedUrl,
    hasExpandableProductImage,
    isImageExpanded,
    handleProductImageClick,
    handleCloseProductImageExpansion,
  } = useInventoryDetailModal({
    open,
    item,
    onEditItem,
    onClose,
    onSave,
    onItemUpdate,
    allowEditInWarehouse,
    allowEditInBranch,
    readOnly,
    initialEditMode,
  });

  const {
    entries: blockHistoryEntries,
    isLoading: isBlockHistoryLoading,
    error: blockHistoryError,
  } = useInventoryBlockHistory({
    open,
    warehouseProductId: item?.originalId,
    refreshKey: blockHistoryRefreshKey,
  });

  const theme = useTheme();

  const footerSecondaryButtonSx = {
    minWidth: '120px',
    padding: '9px 18px',
    borderRadius: '10px',
    textTransform: 'none' as const,
    fontWeight: 500,
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
    border: 'none',
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
      color: theme.palette.text.primary,
    },
  };

  if (!item) return null;

  // Footer actions personalizados
  const footerActions = isEditMode ? (
    <>
      <Button onClick={handleCancelEdit} disabled={isSaving} sx={footerSecondaryButtonSx}>
        Cancelar
      </Button>
      <EditButton onClick={handleSaveClick} disabled={isSaving}>
        Guardar
      </EditButton>
    </>
  ) : (
    <>
      {!embedded && (
        <Button onClick={onClose} sx={footerSecondaryButtonSx}>
          Cerrar
        </Button>
      )}
      {canEdit && (
        <EditButton onClick={handleEditClick}>
          <EditIcon fontSize="small" />
          Editar
        </EditButton>
      )}
    </>
  );

  const detailBody = (
    <>
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {/* Título y estado */}
      <HeaderSection>
        <ProductInfoContainer>
          {/* Imagen del producto */}
          <ImageContainer
            onClick={handleProductImageClick}
            sx={{
              cursor: hasExpandableProductImage ? 'pointer' : 'default',
              '&:hover': hasExpandableProductImage
                ? {
                    transform: 'scale(1.02)',
                    transition: 'transform 0.2s ease-in-out',
                  }
                : {
                    transform: 'none',
                  },
            }}
          >
            {productImageSrc ? (
              <ProductImage src={productImageSrc} alt={item.name} />
            ) : (
              <ProductImagePlaceholder variant="box" size={96} />
            )}
            {hasExpandableProductImage && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  borderRadius: '50%',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}
              >
                <ZoomInIcon sx={{ color: 'common.white', fontSize: '16px' }} />
              </Box>
            )}
          </ImageContainer>

          <ProductDetails>
            <ProductName variant="h5">{item.name}</ProductName>
            <SkuContainer>
              <SkuLabel>Código:</SkuLabel>
              <SkuValue>{item.id}</SkuValue>
            </SkuContainer>
          </ProductDetails>
        </ProductInfoContainer>

        <StatusChip
          label={item.status}
          size="small"
          color={getStatusColor(item.status)}
          icon={<Circle style={{ fontSize: 8 }} />}
          className={getStatusColor(item.status)}
        />
      </HeaderSection>

      <Grid container spacing={4} alignItems="flex-start">
        {/* Columna izquierda */}
        <Grid item xs={12} md={6}>
          {/* Información Básica */}
          <InfoCard>
            <CardHeader sx={(t) => infoCardHeaderSx(t, 'rgb(59, 130, 246)')}>
              <CardTitle sx={(t) => infoCardTitleSx(t, '#1d4ed8')}>
                <Inventory fontSize="small" style={{ marginRight: '8px' }} />
                Información Básica
              </CardTitle>
            </CardHeader>
            <CardContent>
              <InfoGrid container spacing={3}>
                <Grid item xs={6}>
                  <InfoLabel>Código</InfoLabel>
                  <InfoValue>{item.id}</InfoValue>
                </Grid>
                <Grid item xs={6}>
                  <InfoLabel>Nombre</InfoLabel>
                  <InfoValue>{item.name}</InfoValue>
                </Grid>
              </InfoGrid>

              <Box>
                <InfoLabel>Número de Registro</InfoLabel>
                <InfoValue>
                  {item.recordCode != null && item.recordCode !== '' ? item.recordCode : '—'}
                </InfoValue>
              </Box>
            </CardContent>
          </InfoCard>

          {/* Información de Categoría */}
          <InfoCard>
            <CardHeader sx={(t) => infoCardHeaderSx(t, 'rgb(139, 92, 246)')}>
              <CardTitle sx={(t) => infoCardTitleSx(t, '#7c3aed')}>
                <Layers fontSize="small" style={{ marginRight: '8px' }} />
                Categoría
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Box>
                <InfoLabel>Tipo de Producto</InfoLabel>
                <InfoValue>{item.type || 'No especificado'}</InfoValue>
              </Box>

              <Box>
                <InfoLabel>Etiquetas</InfoLabel>
                <TagsContainer>
                  {tagsInfo.hasTags ? (
                    tagsInfo.tags.map((tag: any, index: number) => {
                      const label = typeof tag === 'string' ? tag : (tag?.name ?? tag?.label ?? '');
                      // Asegurarse de obtener el color correctamente
                      let tagColor = '#3b82f6'; // Color por defecto
                      if (typeof tag === 'object' && tag) {
                        tagColor = tag.color || tagColor;
                      }

                      // Debug: ver qué color se está usando

                      return (
                        <TagChip
                          key={index}
                          label={label}
                          size="small"
                          sx={{
                            backgroundColor: `${tagColor}20`,
                            color: tagColor,
                            border: `1px solid ${tagColor}40`,
                          }}
                        />
                      );
                    })
                  ) : (
                    <NoTagsText>Sin etiquetas</NoTagsText>
                  )}
                </TagsContainer>
              </Box>
            </CardContent>
          </InfoCard>

          {/* Precio */}
          <InfoCard>
            <CardHeader sx={(t) => infoCardHeaderSx(t, 'rgb(16, 185, 129)')}>
              <CardTitle sx={(t) => infoCardTitleSx(t, '#059669')}>
                <Money fontSize="small" style={{ marginRight: '8px' }} />
                Precio
              </CardTitle>
            </CardHeader>
            <CardContent>
              <InfoGrid container spacing={3}>
                <Grid item xs={6}>
                  <InfoLabel>Costo Unitario{isEditMode ? ' *' : ''}</InfoLabel>
                  {isEditMode ? (
                    <DecimalNumberInput
                      fullWidth
                      size="small"
                      value={formData.costPrice}
                      onChange={(val) =>
                        handleFieldChange('costPrice', val != null ? String(val) : '')
                      }
                      disabled={isSaving}
                      required
                      decimalPlaces={2}
                      min={0}
                    />
                  ) : (
                    <PriceValue variant="h6" className="cost">
                      {formatPrice((item as any).costPrice || 0)}
                    </PriceValue>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <InfoLabel>Precio de Venta{isEditMode ? ' *' : ''}</InfoLabel>
                  {isEditMode ? (
                    <DecimalNumberInput
                      fullWidth
                      size="small"
                      value={formData.salePrice}
                      onChange={(val) =>
                        handleFieldChange('salePrice', val != null ? String(val) : '')
                      }
                      disabled={isSaving}
                      required
                      decimalPlaces={2}
                      min={0}
                    />
                  ) : (
                    <PriceValue variant="h6" className="sale">
                      {formatPrice(item.price)}
                    </PriceValue>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <InfoLabel>Precio de Venta por Gramo</InfoLabel>
                  <PriceValue variant="h6" className="sale">
                    {formatSalePricePerGram(item.price, item.weightGrams)}
                  </PriceValue>
                </Grid>
                {saleContext != null && !isEditMode && (
                  <Grid item xs={6}>
                    <InfoLabel>Precio Final</InfoLabel>
                    <PriceValue variant="h6" className="sale" sx={{ color: 'success.main' }}>
                      {formatPrice(saleContext.finalPrice)}
                    </PriceValue>
                  </Grid>
                )}
              </InfoGrid>

              {marginInfo &&
                (() => {
                  // #111: when sold, recalculate using actual final price
                  const costPrice = (item as any).costPrice ?? 0;
                  const isSold = saleContext != null;
                  const effectivePrice = isSold
                    ? saleContext.finalPrice
                    : marginInfo.margin + costPrice;
                  const actualMargin = effectivePrice - costPrice;
                  const actualMarginPct =
                    effectivePrice > 0 ? (actualMargin / effectivePrice) * 100 : 0;
                  const isPositive = actualMargin >= 0;
                  return (
                    <InfoGrid container spacing={3} sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <InfoLabel>{isSold ? 'Utilidad' : 'Utilidad Potencial'}</InfoLabel>
                        <InfoValue
                          sx={{
                            fontWeight: 600,
                            color: isPositive
                              ? theme.palette.success.main
                              : theme.palette.error.main,
                          }}
                        >
                          {isPositive ? '+' : ''}
                          {formatPrice(actualMargin)}
                        </InfoValue>
                      </Grid>
                      <Grid item xs={6}>
                        <InfoLabel>{isSold ? 'Margen' : 'Margen Potencial'}</InfoLabel>
                        <InfoValue
                          sx={{
                            fontWeight: 600,
                            color: isPositive
                              ? theme.palette.success.main
                              : theme.palette.error.main,
                          }}
                        >
                          {actualMarginPct.toFixed(2)}%
                        </InfoValue>
                      </Grid>
                    </InfoGrid>
                  );
                })()}
            </CardContent>
          </InfoCard>
        </Grid>

        {/* Columna derecha */}
        <Grid item xs={12} md={6}>
          {/* Información de Propiedades */}
          <InfoCard>
            <CardHeader sx={(t) => infoCardHeaderSx(t, 'rgb(245, 158, 11)')}>
              <CardTitle sx={(t) => infoCardTitleSx(t, '#d97706')}>
                <Tag fontSize="small" style={{ marginRight: '8px' }} />
                Propiedades
              </CardTitle>
            </CardHeader>
            <CardContent>
              <InfoGrid container spacing={3}>
                <Grid item xs={6}>
                  <InfoLabel>Material</InfoLabel>
                  {isEditMode ? (
                    <FormControl fullWidth size="small">
                      <Select
                        value={formData.materialId || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleFieldChange('materialId', value);
                        }}
                        disabled={isSaving || isLoadingMaterials}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>Selecciona un material</em>
                        </MenuItem>
                        {materials.map((material) => (
                          <MenuItem key={material.id} value={material.id.toString()}>
                            {material.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <InfoValue>{propertiesInfo.material}</InfoValue>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <InfoLabel>Peso (g)</InfoLabel>
                  {isEditMode ? (
                    <DecimalNumberInput
                      fullWidth
                      size="small"
                      value={formData.weightGrams}
                      onChange={(val) =>
                        handleFieldChange('weightGrams', val != null ? String(val) : '')
                      }
                      disabled={isSaving}
                      decimalPlaces={2}
                      min={0}
                    />
                  ) : (
                    <InfoValue>{propertiesInfo.weight}</InfoValue>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <InfoLabel>Largo (cm)</InfoLabel>
                  {isEditMode ? (
                    <DecimalNumberInput
                      fullWidth
                      size="small"
                      value={formData.lengthMm}
                      onChange={(val) =>
                        handleFieldChange('lengthMm', val != null ? String(val) : '')
                      }
                      disabled={isSaving}
                      decimalPlaces={2}
                      min={0}
                    />
                  ) : (
                    <InfoValue>{propertiesInfo.length}</InfoValue>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <InfoLabel>Talla</InfoLabel>
                  {isEditMode ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={formData.diameterSize}
                      onChange={(e) => handleFieldChange('diameterSize', e.target.value)}
                      disabled={isSaving}
                    />
                  ) : (
                    <InfoValue>{propertiesInfo.diameter}</InfoValue>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <InfoLabel>Color</InfoLabel>
                  {isEditMode ? (
                    <FormControl fullWidth size="small">
                      <Select
                        value={formData.colorId || ''}
                        onChange={(e) => handleFieldChange('colorId', e.target.value)}
                        disabled={isSaving || isLoadingColors}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>Selecciona un color</em>
                        </MenuItem>
                        {colors.map((color) => (
                          <MenuItem key={color.id} value={color.id.toString()}>
                            {color.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <InfoValue>{propertiesInfo.color}</InfoValue>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <InfoLabel>Código Proveedor</InfoLabel>
                  {isEditMode ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={formData.workshopCode}
                      onChange={(e) => handleFieldChange('workshopCode', e.target.value)}
                      disabled={isSaving}
                    />
                  ) : (
                    <InfoValue>{propertiesInfo.workshopCode}</InfoValue>
                  )}
                </Grid>
              </InfoGrid>
            </CardContent>
          </InfoCard>

          {/* Ubicación */}
          <InfoCard>
            <CardHeader sx={(t) => infoCardHeaderSx(t, 'rgb(245, 158, 11)')}>
              <CardTitle sx={(t) => infoCardTitleSx(t, '#d97706')}>
                <LocationOn fontSize="small" style={{ marginRight: '8px' }} />
                Ubicación
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Box>
                <InfoLabel>Ubicación</InfoLabel>
                <InfoValue>{locationInfo.locationName}</InfoValue>
              </Box>
            </CardContent>
          </InfoCard>

          {/* Proveedor */}
          <InfoCard>
            <CardHeader sx={(t) => infoCardHeaderSx(t, 'rgb(34, 197, 94)')}>
              <CardTitle sx={(t) => infoCardTitleSx(t, '#16a34a')}>
                <Business fontSize="small" style={{ marginRight: '8px' }} />
                Proveedor
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Box>
                <InfoLabel>Nombre del Proveedor</InfoLabel>
                <InfoValue>{(item as any).supplier?.name || 'N/A'}</InfoValue>
              </Box>
              {(item as any).supplier?.status && (
                <Box>
                  <InfoLabel>Estado</InfoLabel>
                  <InfoValue>
                    {(item as any).supplier.status === 'ACTIVE' ? 'Activo' : 'Inactivo'}
                  </InfoValue>
                </Box>
              )}
            </CardContent>
          </InfoCard>

          {/* Estado y Fechas */}
          <InfoCard>
            <CardHeader sx={(t) => infoCardHeaderSx(t, 'rgb(79, 70, 229)')}>
              <CardTitle sx={(t) => infoCardTitleSx(t, '#4f46e5')}>
                <Business fontSize="small" style={{ marginRight: '8px' }} />
                Estado y Fechas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <InfoGrid container spacing={3}>
                <Grid item xs={6}>
                  <InfoLabel>Estado Inventario</InfoLabel>
                  <StatusChip
                    label={item.status}
                    size="small"
                    color={getStatusColor(item.status)}
                    className={getStatusColor(item.status)}
                  />
                </Grid>
                {item.availabilityStatus != null && item.availabilityStatus !== '' && (
                  <Grid item xs={6}>
                    <InfoLabel>Estado Venta</InfoLabel>
                    <StatusChip
                      label={item.availabilityStatus}
                      size="small"
                      color={mapInventoryAvailability(item.availabilityStatus).color}
                    />
                  </Grid>
                )}
                <Grid item xs={6}>
                  <InfoLabel>Fecha de registro</InfoLabel>
                  <InfoValue>{formatDate(datesInfo.createdAt)}</InfoValue>
                </Grid>
                <Grid item xs={6}>
                  <InfoLabel>Fecha de Transferencia</InfoLabel>
                  <InfoValue>{formatDate(datesInfo.transferAt)}</InfoValue>
                </Grid>
                {saleContext != null && (
                  <Grid item xs={6}>
                    <InfoLabel>Fecha de Venta</InfoLabel>
                    <InfoValue>
                      {formatCommercialSaleDate(
                        { saleDate: saleContext.saleDate, createdAt: saleContext.saleDate },
                        'es-ES',
                        { includeTime: true },
                      )}
                    </InfoValue>
                  </Grid>
                )}
              </InfoGrid>
            </CardContent>
          </InfoCard>
        </Grid>
      </Grid>

      <InventoryBlockHistorySection
        entries={blockHistoryEntries}
        isLoading={isBlockHistoryLoading}
        error={blockHistoryError}
      />
    </>
  );

  const auxiliaryModals = (
    <>
      <ImageModal
        isOpen={isImageExpanded}
        onClose={handleCloseProductImageExpansion}
        imageUrl={productImageExpandedUrl ?? ''}
        altText={item.name}
      />

      {!readOnly && (
        <ConfirmationModal
          open={showConfirmDialog}
          onClose={() => !isSaving && setShowConfirmDialog(false)}
          onConfirm={handleConfirmSave}
          title="¿Desea guardar los cambios?"
          confirmText={isSaving ? 'Guardando...' : 'Confirmar y Guardar'}
          cancelText="Cancelar"
          variant="default"
          icon={<Inventory />}
          loading={isSaving}
          customContent={
            <Box
              sx={(t) => ({
                p: 2.5,
                borderRadius: '8px',
                backgroundColor: t.palette.action.hover,
                border: `1px solid ${t.palette.divider}`,
                textAlign: 'left',
              })}
            >
              <Typography
                variant="caption"
                sx={(t) => ({
                  color: t.palette.text.secondary,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  display: 'block',
                  mb: 1,
                })}
              >
                Pieza
              </Typography>
              <Typography
                variant="body1"
                sx={(t) => ({
                  fontWeight: 600,
                  color: t.palette.text.primary,
                  mb: 1.5,
                })}
              >
                {item.name}
              </Typography>
              <Typography
                variant="caption"
                sx={(t) => ({
                  color: t.palette.text.secondary,
                  fontSize: '0.8125rem',
                })}
              >
                Código: <strong>{item.id}</strong>
              </Typography>
            </Box>
          }
        />
      )}
    </>
  );

  if (embedded) {
    return (
      <>
        {detailBody}
        {auxiliaryModals}
      </>
    );
  }

  return (
    <>
      <DetailModal
        open={open}
        onClose={onClose}
        title="Detalle del Inventario"
        icon={<Inventory style={{ marginRight: '8px' }} />}
        maxWidth="md"
        footerActions={footerActions}
      >
        {detailBody}
      </DetailModal>
      {auxiliaryModals}
    </>
  );
};

export default InventoryDetailModal;
